// The Excel report's task cells (Documentation/excel-report): the value a visit, application or activity gets in
// the customer's template, where a date means "done" - their whole sorting rests on that:
// - done: the done date as a real Excel date (shown DD-MM-YYYY)
// - else cancelled: the text "Cancelled"
// - else its Planned value (any of the five ways): text "P " + the value as written, e.g. "P medio May 2027"
// - and its Time interval: text "TI " + its date and/or text as written ("TI 15-04-2027 · BBCH 31-32", "TI 7 DAA") - but
//   missing info ("?", "Maybe", "TBD") and "Not needed" without a date as written, as in the design (Designforslag_Week43)
// - both: two lines in the cell, "P ..." on top, "TI ..." below; neither: an empty cell
// Planned and Time interval are never written as dates, so they never look done.
import { formatDate } from '../dateFormat';
import { expectedText, timeIntervalParts } from '../planning';
import { isMissingValue, isNotNeededValue } from './cellStatus';

/** A done date ("2026-05-04") as the Date Excel shows as that day: UTC midnight, so no timezone shifts it. */
export function excelDate(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd ?? ''));
  return m ? new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))) : null;
}

const blank = (value) => value === null || value === undefined || String(value).trim() === '';

/**
 * The cell value of a task ({ donedate, cancelled, planned_interval, planned_date, timeinterval }) - a Date, a
 * string or null; null too without a task.
 */
export function taskCell(task) {
  if (!task) return null;
  if (task.donedate) return excelDate(task.donedate);
  if (task.cancelled) return 'Cancelled';
  const plannedValue = plannedText(task);
  const planned = plannedValue === null ? null : `P ${plannedValue}`;
  const interval = timeIntervalCell(task);
  // Both: two lines in the cell, Planned on top
  if (planned && interval) return `${planned}\n${interval}`;
  return planned ?? interval;
}

/** A task's Time interval as written: "<date> · <text>" (a date DD-MM-YYYY), either alone, or null. */
export const timeIntervalText = (task) => expectedText(task) || null;

/**
 * A task's Time interval cell text: "TI " + its date and/or text; missing info ("?") and "Not needed" without a date as
 * written; null without one. (All data and the layout sheet alike.)
 */
export function timeIntervalCell(task) {
  const shown = timeIntervalText(task);
  if (shown === null) return null;
  const { date, text } = timeIntervalParts(task);
  return !date && (isMissingValue(text) || isNotNeededValue(text)) ? text : `TI ${shown}`;
}
/** A task's Planned value as written (a date DD-MM-YYYY), or null. */
export const plannedText = (task) => {
  const planned = task.planned_interval || task.planned_date;
  return blank(planned) ? null : formatDate(planned);
};

/** Whether a cell value means "done" (a date) - the template's rule, which the work sheets follow. */
export const isDone = (value) => value instanceof Date;

/**
 * The one task of several with the same number on a trial (old data has some): the done one, else the newest.
 */
export function pickTask(tasks) {
  if (!tasks.length) return null;
  const done = tasks.filter((t) => t.donedate);
  const from = done.length ? done : tasks;
  return from.reduce((a, b) => (b.id > a.id ? b : a));
}

export const APPLICATION_LETTERS = 'ABCDEFGHIJKL'.split('');
export const VISIT_NUMBERS = Array.from({ length: 15 }, (_, n) => `V${String(n).padStart(2, '0')}`);

/** The report's activity columns: their VA codes. */
export const ACTIVITY_VANOS = {
  reservedArea: 'A10', stackedOut: 'A11', plotSize: 'A12', bordersSprayed: 'A13', harvest: 'A32', destruction: 'A33', pegsUp: 'A34',
};

const groupBy = (items, key) => items.reduce((map, item) => {
  const k = key(item);
  if (k !== null) map.set(k, [...(map.get(k) ?? []), item]);
  return map;
}, new Map());

/**
 * A trial's tasks by the report's columns: { applications: { A: task, ... }, visits: { V00: task, ... },
 * activities: { A13: task, ... }, notShown, duplicates } - visits 1 (V00-V14, "3" counts as V03), applications 2
 * (A-L), activities by code. notShown counts the visits and applications without a column (V15+, M-Z, other
 * numbers); duplicates the tasks not shown because another one has the same column (pickTask).
 */
export function tasksByColumn(tasks = []) {
  let notShown = 0;
  const visitKey = (t) => {
    if (Number(t.va_id) !== 1) return null;
    const n = /^\d{1,2}$/.test(String(t.vano ?? '').trim()) ? Number(t.vano) : null;
    if (n === null || n > 14) { notShown++; return null; }
    return VISIT_NUMBERS[n];
  };
  const applicationKey = (t) => {
    if (Number(t.va_id) !== 2) return null;
    const letter = String(t.vano ?? '').trim().toUpperCase();
    if (!APPLICATION_LETTERS.includes(letter)) { notShown++; return null; }
    return letter;
  };
  // (old applications numbered e.g. "A13" are applications, not the activity A13)
  const activityKey = (t) => ([1, 2].includes(Number(t.va_id)) || !Object.values(ACTIVITY_VANOS).includes(t.vano) ? null : t.vano);
  let duplicates = 0;
  const pick = (map) => Object.fromEntries([...map].map(([k, v]) => {
    duplicates += v.length - 1;
    return [k, pickTask(v)];
  }));
  return {
    visits: pick(groupBy(tasks, visitKey)),
    applications: pick(groupBy(tasks, applicationKey)),
    activities: pick(groupBy(tasks, activityKey)),
    notShown,
    duplicates,
  };
}
